import { ref, get, update } from 'firebase/database';
import { database } from './core';
import logger from '../../utils/logger';
import { getCurrentUser } from './auth.service';
import {
  setData,
  updateData,
  removeData,
  createWithKey,
  updateMultipleSafe
} from './api-wrapper.service';
import { cleanForFirebase } from '../../utils/firebase';

/**
 * Interface for a technique in the training wiki
 */
export interface TechniqueData {
  id: string;
  name: string;
  sortname?: string; // Lowercase version of name for sorting
  description?: string;
  category?: string;
  position?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  tags?: string[];
  videoUrl?: string;
  imageUrl?: string;
  isDefault?: boolean; // true if it's a pre-loaded technique
  createdBy?: string; // userId of creator
  creatorId?: string; // Alias for createdBy for UI consistency
  createdAt?: number; // timestamp
  dateCreated?: number; // Alias for createdAt for UI consistency
  updatedAt?: number; // timestamp
  popularity?: number; // For sorting by popularity
  // Additional fields from original Marune
  type?: string; // Original category field name
  publicInfo?: any; // Public wiki information
}

/**
 * Interface for a timestamped note
 */
export interface TechniqueNote {
  id?: string;
  text: string;
  dateCreated: number;
  dateUpdated: number;
}

/**
 * Interface for a photo in the media gallery
 */
export interface TechniquePhoto {
  id?: string;
  url: string;
  thumbnail?: string;
  dateAdded: number;
  caption?: string;
}

/**
 * Interface for a technique in a user's collection
 */
export interface UserTechnique {
  id?: string;  // Used in the UI for identification (same as techniqueId)
  techniqueId: string;
  userId: string;
  notes?: string | Record<string, TechniqueNote>;  // Can be string or timestamped notes
  personalNotes?: string;  // Additional notes the user adds for their personal reference
  favorite?: boolean;
  savedAt: number;
  dateAdded?: number;  // Alias for savedAt for UI consistency
  lastPracticedAt?: number;
  lastUsed?: number;  // Alias for lastPracticedAt for UI consistency
  lastTrainedOn?: number; // Original field name
  practicedCount?: number;
  timesUsed?: number;  // Alias for practicedCount for UI consistency
  timesTrained?: number; // Original field name
  isPublic?: boolean;  // From personalWiki structure
  isVisible?: boolean;  // From personalWiki structure
  isTrainingWikiTech?: boolean;  // From personalWiki structure
  photos?: Record<string, TechniquePhoto>; // User's photos for this technique
}

/**
 * Service for interacting with the training wiki in Firebase
 */
export const getAllTechniques = async (): Promise<Record<string, TechniqueData>> => {
  try {
    const allTechniques: Record<string, TechniqueData> = {};
    
    // Only get techniques from original Marune structure (trainingWikiTechniques)
    // Users don't have permission to read from /techniques node
    const twtRef = ref(database, 'trainingWikiTechniques');
    const twtSnapshot = await get(twtRef);
    
    if (twtSnapshot.exists()) {
      const twtData = twtSnapshot.val();
      
      Object.entries(twtData).forEach(([id, techData]: [string, any]) => {
        if (techData && typeof techData === 'object') {
          const techniqueData = techData.techniqueData || {};
          const publicData = techData.public || {};
          
          // Merge or add technique data
          const techName = techniqueData.name || publicData.name || allTechniques[id]?.name || '';
          allTechniques[id] = {
            id,
            name: techName,
            sortname: techName.toLowerCase(),
            description: publicData.description || techniqueData.description || allTechniques[id]?.description || '',
            category: techniqueData.type || techniqueData.category || publicData.category || allTechniques[id]?.category || '',
            position: techniqueData.position || publicData.position || allTechniques[id]?.position || '',
            difficulty: techniqueData.difficulty || publicData.difficulty || allTechniques[id]?.difficulty || 'beginner',
            videoUrl: publicData.videoUrl || publicData.video || allTechniques[id]?.videoUrl || '',
            imageUrl: publicData.imageUrl || publicData.image || allTechniques[id]?.imageUrl || '',
            tags: publicData.tags || techniqueData.tags || allTechniques[id]?.tags || [],
            isDefault: true,
            createdBy: techniqueData.createdBy || publicData.createdBy || allTechniques[id]?.createdBy || 'system',
            createdAt: techniqueData.dateCreated || publicData.dateCreated || allTechniques[id]?.createdAt || Date.now(),
            popularity: allTechniques[id]?.popularity || 0
          };
        }
      });
    }
    
    return allTechniques;
  } catch (error) {
    logger.error('Error fetching techniques:', error);
    throw error;
  }
};

/**
 * Adds a new note to a technique in the user's personal wiki
 * Following exact pattern from original app: personalWiki.service.js
 * @param techKey The key of the technique
 * @param noteData Note data containing text
 */
export const addNoteToWikiTechnique = async (techKey: string, noteData: { text: string }): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const currentTime = Date.now();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _noteRef = ref(database, `personalWiki/${user.uid}/${techKey}/notes`);

    // Push new note with auto-generated key
    // Clean noteData to remove undefined values before writing to Firebase
    await createWithKey(
      `personalWiki/${user.uid}/${techKey}/notes`,
      {
        dateCreated: currentTime,
        dateUpdated: currentTime,
        ...cleanForFirebase(noteData)
      }
    );
    
    logger.info(`Added note to technique ${techKey}`);
  } catch (error) {
    logger.error(`Error adding note to technique ${techKey}:`, error);
    throw error;
  }
};

/**
 * Edits an existing note for a technique
 * Following exact pattern from original app: personalWiki.service.js
 * @param techKey The key of the technique
 * @param noteKey The key of the note to edit
 * @param newData Updated note data
 */
export const editWikiTechniqueNote = async (techKey: string, noteKey: string, newData: { text: string }): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Clean newData to remove undefined values before writing to Firebase
    await updateData(
      `personalWiki/${user.uid}/${techKey}/notes/${noteKey}`,
      {
        dateUpdated: Date.now(),
        ...cleanForFirebase(newData)
      }
    );
    
    logger.info(`Updated note ${noteKey} for technique ${techKey}`);
  } catch (error) {
    logger.error(`Error updating note ${noteKey} for technique ${techKey}:`, error);
    throw error;
  }
};

/**
 * Deletes a note from a technique
 * @param techKey The key of the technique
 * @param noteKey The key of the note to delete
 */
export const deleteWikiTechniqueNote = async (techKey: string, noteKey: string): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    await removeData(`personalWiki/${user.uid}/${techKey}/notes/${noteKey}`);
    
    logger.info(`Deleted note ${noteKey} from technique ${techKey}`);
  } catch (error) {
    logger.error(`Error deleting note ${noteKey} from technique ${techKey}:`, error);
    throw error;
  }
};

/**
 * Gets Training Wiki Technique data
 * Following exact pattern from original app API
 * @param techKey The key of the technique
 * @returns Technique data or null if not a TWT
 */
export const getTWTData = async (techKey: string): Promise<any> => {
  try {
    // Check if this technique exists in the Training Wiki Techniques collection
    const twtRef = ref(database, `trainingWikiTechniques/${techKey}`);
    const snapshot = await get(twtRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    
    return null;
  } catch (error) {
    logger.error(`Error getting TWT data for ${techKey}:`, error);
    return null;
  }
};

/**
 * Adds a technique to user's personal wiki
 * Following exact pattern from original app: personalWiki.service.js
 * @param techKey Key of the technique to add
 * @param isTWT true if this is a Training Wiki Technique
 */
export const addTechToWiki = async (techKey: string, isTWT: boolean): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const wikiData = {
      dateAdded: Date.now(),
      lastTrainedOn: Date.now(),
      isPublic: false,
      isVisible: true,
      isTrainingWikiTech: isTWT
    };
    
    await updateData(`personalWiki/${user.uid}/${techKey}`, wikiData);
    
    logger.info(`Added technique ${techKey} to personal wiki`);
  } catch (error) {
    logger.error(`Error adding technique ${techKey} to wiki:`, error);
    throw error;
  }
};

export const getTechniqueById = async (techniqueId: string): Promise<TechniqueData | null> => {
  try {
    // Only get from trainingWikiTechniques (original structure)
    // Users don't have permission to read from /techniques node
    const twtRef = ref(database, `trainingWikiTechniques/${techniqueId}`);
    const twtSnapshot = await get(twtRef);
    
    if (twtSnapshot.exists()) {
      const twtData = twtSnapshot.val();
      
      // Get technique data
      const techData = twtData.techniqueData || {};
      
      // Get public data
      const publicData = twtData.public || {};
      
      // Merge all data
      const techName = techData.name || publicData.name || '';
      return {
        id: techniqueId,
        name: techName,
        sortname: techName.toLowerCase(),
        description: publicData.description || techData.description || '',
        category: techData.type || techData.category || publicData.category || '',
        position: techData.position || publicData.position || '',
        difficulty: techData.difficulty || publicData.difficulty || 'beginner',
        videoUrl: publicData.videoUrl || publicData.video || '',
        imageUrl: publicData.imageUrl || publicData.image || '',
        tags: publicData.tags || techData.tags || [],
        isDefault: true,
        createdBy: techData.createdBy || publicData.createdBy || 'system',
        createdAt: techData.dateCreated || publicData.dateCreated || Date.now()
      };
    }
    
    return null;
  } catch (error) {
    logger.error(`Error fetching technique ${techniqueId}:`, error);
    throw error;
  }
};

export const getUserTechniques = async (userId: string): Promise<Record<string, UserTechnique>> => {
  try {
    logger.info(`Fetching user techniques for user: ${userId}`);
    
    // Use personalWiki path to match original Marune app structure
    const personalWikiRef = ref(database, `personalWiki/${userId}`);
    const snapshot = await get(personalWikiRef);
    
    if (snapshot.exists()) {
      const personalWikiData = snapshot.val();
      logger.info(`Found ${Object.keys(personalWikiData).length} techniques in personalWiki for user ${userId}`);
      
      // Transform personalWiki data to UserTechnique format
      const userTechniques: Record<string, UserTechnique> = {};
      
      Object.entries(personalWikiData).forEach(([techniqueId, data]: [string, any]) => {
        logger.info(`Processing technique ${techniqueId} with data:`, data);
        userTechniques[techniqueId] = {
          id: techniqueId,
          techniqueId,
          userId,
          notes: data.notes || '',
          personalNotes: typeof data.notes === 'string' ? data.notes : '',
          favorite: data.favorite || false,
          savedAt: data.dateAdded || Date.now(),
          dateAdded: data.dateAdded || Date.now(),
          lastPracticedAt: data.lastTrainedOn || data.lastUsedOn,
          lastUsed: data.lastTrainedOn || data.lastUsedOn,
          lastTrainedOn: data.lastTrainedOn,
          practicedCount: data.timesUsed || data.timesTrained || 0,
          timesUsed: data.timesUsed || data.timesTrained || 0,
          timesTrained: data.timesTrained,
          isPublic: data.isPublic || false,
          isVisible: data.isVisible !== false, // Default to true
          isTrainingWikiTech: data.isTrainingWikiTech || false,
          photos: data.photos || {}
        };
      });
      
      logger.info(`Returning ${Object.keys(userTechniques).length} user techniques`);
      return userTechniques;
    }
    
    logger.info(`No techniques found in personalWiki for user ${userId}`);
    return {};
  } catch (error) {
    logger.error(`Error fetching user techniques for ${userId}:`, error);
    throw error;
  }
};

export const createTechnique = async (technique: Omit<TechniqueData, 'id'>): Promise<TechniqueData> => {
  try {
    const now = Date.now();

    // Structure it like original Marune with techniqueData node
    const techniqueData = {
      techniqueData: {
        name: technique.name,
        sortname: technique.name.toLowerCase(), // For sorting
        type: technique.category,
        createdBy: technique.createdBy,
        dateCreated: now,
        isDefault: false
      }
    };

    // Create techniques in trainingWikiTechniques following original Marune structure
    const techniqueId = await createWithKey('trainingWikiTechniques', techniqueData);

    if (!techniqueId) {
      throw new Error('Failed to generate technique ID');
    }

    const newTechnique: TechniqueData = {
      ...technique,
      id: techniqueId,
      sortname: technique.name.toLowerCase(),
      createdAt: now,
      dateCreated: now, // Alias for UI consistency
      updatedAt: now,
      popularity: 0,
      creatorId: technique.createdBy // Set creatorId same as createdBy for consistency
    };

    return newTechnique;
  } catch (error) {
    logger.error('Error creating technique:', error);
    throw error;
  }
};

/**
 * Creates a user technique in /techniques path (community techniques)
 * Following exact pattern from original app: AddTechniques.screen.js
 *
 * Original code:
 * ```javascript
 * const newTechnique = {
 *   isDefault: false,
 *   name: techName,
 *   sortname: techName.toLowerCase(),
 *   user: this.props.uid,
 * };
 * const pushKey = firebase.database().ref('techniques').push(newTechnique).key;
 * ```
 *
 * @param name - The technique name
 * @param userId - The user's UID (stored as `user` field)
 * @returns Object with technique id and data
 */
export const createUserTechnique = async (
  name: string,
  userId: string
): Promise<{ id: string; name: string; sortname: string; isDefault: boolean; user: string }> => {
  try {
    // Exact structure from original Marune app
    const techniqueData = {
      isDefault: false,
      name: name,
      sortname: name.toLowerCase(),
      user: userId  // Original app uses 'user' field, not 'createdBy'
    };

    // Create in /techniques path (community techniques)
    const techniqueId = await createWithKey('techniques', techniqueData);

    if (!techniqueId) {
      throw new Error('Failed to generate technique ID');
    }

    logger.info(`[createUserTechnique] Created user technique: ${techniqueId} - ${name}`);

    return {
      id: techniqueId,
      ...techniqueData
    };
  } catch (error) {
    logger.error('Error creating user technique:', error);
    throw error;
  }
};

export const updateTechnique = async (
  techniqueId: string, 
  updates: Partial<TechniqueData>
): Promise<TechniqueData> => {
  try {
    // Update in trainingWikiTechniques structure
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _techniqueRef = ref(database, `trainingWikiTechniques/${techniqueId}/techniqueData`);
    
    // Add updatedAt timestamp
    const updatedTechnique = {
      ...updates,
      updatedAt: Date.now()
    };
    
    await updateData(`trainingWikiTechniques/${techniqueId}/techniqueData`, updatedTechnique);
    
    // Fetch the updated technique
    const result = await getTechniqueById(techniqueId);
    if (!result) {
      throw new Error(`Technique ${techniqueId} not found after update`);
    }
    
    return result;
  } catch (error) {
    logger.error(`Error updating technique ${techniqueId}:`, error);
    throw error;
  }
};

export const deleteTechnique = async (techniqueId: string): Promise<void> => {
  try {
    // Delete from trainingWikiTechniques
    await removeData(`trainingWikiTechniques/${techniqueId}`);
  } catch (error) {
    logger.error(`Error deleting technique ${techniqueId}:`, error);
    throw error;
  }
};

export const saveTechniqueToCollection = async (
  userId: string, 
  techniqueId: string,
  notes?: string
): Promise<UserTechnique> => {
  try {
    // Use personalWiki path to match original Marune app structure
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _personalWikiRef = ref(database, `personalWiki/${userId}/${techniqueId}`);
    
    // Don't check if technique exists in trainingWikiTechniques to avoid permission errors
    // Instead, assume it's a Training Wiki Tech if it starts with -L (Firebase push key pattern)
    let isTrainingWikiTech = techniqueId.startsWith('-L');
    
    const now = Date.now();
    // Match original Marune personalWiki data structure
    const wikiData: any = {
      dateAdded: now,
      lastTrainedOn: now,
      isPublic: false,
      isVisible: true,
      isTrainingWikiTech
    };
    
    // Add personal notes if provided
    if (notes) {
      wikiData.personalNotes = notes;
    }
    
    await setData(`personalWiki/${userId}/${techniqueId}`, wikiData);
    
    // Also update the usersTrainingWikiTechsList helper node if it's a TWTech
    if (isTrainingWikiTech) {
      await setData(`usersTrainingWikiTechsList/${userId}/${techniqueId}`, true);
    }
    
    // Note: We don't update technique popularity here as users don't have write permission to /techniques
    // This would need to be done via a Cloud Function or admin operation
    
    // Return in UserTechnique format for consistency with Phoenix
    const userTechnique: UserTechnique = {
      id: techniqueId,
      techniqueId,
      userId,
      notes,
      personalNotes: notes,
      savedAt: now,
      dateAdded: now,
      favorite: false,
      practicedCount: 0,
      timesUsed: 0,
      lastPracticedAt: now,
      lastUsed: now
    };
    
    return userTechnique;
  } catch (error) {
    logger.error(`Error saving technique ${techniqueId} to user ${userId}:`, error);
    throw error;
  }
};

export const removeTechniqueFromCollection = async (
  userId: string, 
  techniqueId: string
): Promise<void> => {
  try {
    // Remove from personalWiki and helper list (match original Marune app)
    const updates = {
      [`personalWiki/${userId}/${techniqueId}`]: null,
      [`usersTrainingWikiTechsList/${userId}/${techniqueId}`]: null
    };
    
    // Use safer version to avoid root permission errors
    await updateMultipleSafe(updates);
    
    // Note: We don't update technique popularity here as users don't have write permission to /techniques
    // This would need to be done via a Cloud Function or admin operation
  } catch (error) {
    logger.error(`Error removing technique ${techniqueId} from user ${userId}:`, error);
    throw error;
  }
};

/**
 * Updates or adds techniques to user's personal wiki when used in training flows
 * This mimics the original Marune app behavior where techniques used in flows
 * are automatically added to the user's personal wiki
 */
export const updateTechniquesFromTrainingFlow = async (
  userId: string,
  techniqueIds: string[],
  techniqueDetails?: Record<string, { name: string; isTrainingWikiTech: boolean }>
): Promise<void> => {
  logger.info(`updateTechniquesFromTrainingFlow called for user ${userId} with ${techniqueIds?.length || 0} techniques`);
  
  if (!techniqueIds || techniqueIds.length === 0) {
    logger.info('No technique IDs provided, returning early');
    return;
  }

  try {
    const now = Date.now();
    const updates: Record<string, any> = {};

    for (const techniqueId of techniqueIds) {
      const personalWikiPath = `personalWiki/${userId}/${techniqueId}`;
      logger.info(`Processing technique ${techniqueId} at path ${personalWikiPath}`);
      
      // Get existing data if any
      const existingRef = ref(database, personalWikiPath);
      const existingSnapshot = await get(existingRef);
      
      if (existingSnapshot.exists()) {
        // Technique already in personal wiki - just update lastTrainedOn and increment timesTrained
        const existingData = existingSnapshot.val();
        logger.info(`Technique ${techniqueId} already exists, updating timesTrained`);
        updates[personalWikiPath] = {
          ...existingData,
          lastTrainedOn: now,
          timesTrained: (existingData.timesTrained || 0) + 1
        };
      } else {
        // New technique - add to personal wiki
        const details = techniqueDetails?.[techniqueId];
        const isTrainingWikiTech = details?.isTrainingWikiTech ?? techniqueId.startsWith('-L');
        
        logger.info(`Adding new technique ${techniqueId} to personal wiki, isTrainingWikiTech: ${isTrainingWikiTech}`);
        
        updates[personalWikiPath] = {
          dateAdded: now,
          lastTrainedOn: now,
          timesTrained: 1,
          isPublic: false,
          isVisible: true,
          isTrainingWikiTech
          // Don't store name - the original app fetches it from trainingWikiTechniques
        };
        
        // Also add to helper list if it's a TWTech
        if (isTrainingWikiTech) {
          updates[`usersTrainingWikiTechsList/${userId}/${techniqueId}`] = true;
        }
      }
    }

    // Apply updates individually to avoid root permission errors
    logger.info(`Applying ${Object.keys(updates).length} updates to Firebase`);
    
    // Update each path individually
    for (const [path, data] of Object.entries(updates)) {
      try {
        await updateData(path, data);
      } catch (updateError) {
        logger.error(`Failed to update ${path}:`, updateError);
        // Continue with other updates even if one fails
      }
    }
    
    logger.info(`Successfully updated ${techniqueIds.length} techniques in personal wiki for user ${userId}`);
  } catch (error) {
    logger.error('Error updating techniques from training flow:', error);
    // Don't throw - this is a non-critical enhancement
  }
};

export const updateUserTechnique = async (
  userId: string,
  techniqueId: string,
  updates: Partial<UserTechnique> & { personalNotes?: string }
): Promise<UserTechnique> => {
  try {
    // Use personalWiki path to match original Marune app structure
    const personalWikiRef = ref(database, `personalWiki/${userId}/${techniqueId}`);
    
    // Transform updates to match personalWiki structure
    const wikiUpdates: any = {};
    if (updates.personalNotes !== undefined || updates.notes !== undefined) {
      wikiUpdates.notes = updates.personalNotes || updates.notes;
    }
    if (updates.favorite !== undefined) {
      wikiUpdates.favorite = updates.favorite;
    }
    if (updates.lastPracticedAt !== undefined || updates.lastUsed !== undefined) {
      wikiUpdates.lastTrainedOn = updates.lastPracticedAt || updates.lastUsed;
    }
    if (updates.practicedCount !== undefined || updates.timesUsed !== undefined) {
      wikiUpdates.timesUsed = updates.practicedCount || updates.timesUsed;
    }
    
    await updateData(`personalWiki/${userId}/${techniqueId}`, wikiUpdates);
    
    // Fetch the updated data and transform back to UserTechnique format
    const snapshot = await get(personalWikiRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      return {
        id: techniqueId,
        techniqueId,
        userId,
        notes: data.notes || '',
        personalNotes: data.notes || '',
        favorite: data.favorite || false,
        savedAt: data.dateAdded || Date.now(),
        dateAdded: data.dateAdded || Date.now(),
        lastPracticedAt: data.lastTrainedOn || data.lastUsedOn,
        lastUsed: data.lastTrainedOn || data.lastUsedOn,
        practicedCount: data.timesUsed || 0,
        timesUsed: data.timesUsed || 0
      };
    }
    
    throw new Error(`User technique ${techniqueId} for user ${userId} not found after update`);
  } catch (error) {
    logger.error(`Error updating user technique ${techniqueId} for user ${userId}:`, error);
    throw error;
  }
};

export const searchTechniques = async (searchTerm: string): Promise<TechniqueData[]> => {
  try {
    // Get all techniques and filter them
    const allTechniques = await getAllTechniques();
    const searchTermLower = searchTerm.toLowerCase();
    
    // Filter techniques by name, description, category, or tags
    return Object.values(allTechniques).filter((technique) => {
      // Ensure technique is a valid TechniqueData object
      if (!technique || typeof technique !== 'object' || !('name' in technique)) {
        return false;
      }
      
      // Cast technique to the correct type
      const techniqueCast = technique as TechniqueData;
      
      const name = (techniqueCast.name || '').toLowerCase();
      const description = (techniqueCast.description || '').toLowerCase();
      const category = (techniqueCast.category || '').toLowerCase();
      const tags = (techniqueCast.tags || []).map((tag: string) => tag.toLowerCase());
      
      return (
        name.includes(searchTermLower) ||
        description.includes(searchTermLower) ||
        category.includes(searchTermLower) ||
        tags.some((tag: string) => tag.includes(searchTermLower))
      );
    }) as TechniqueData[];
  } catch (error) {
    logger.error(`Error searching techniques for "${searchTerm}":`, error);
    throw error;
  }
};

export const getTechniquesByCategory = async (category: string): Promise<TechniqueData[]> => {
  try {
    const allTechniques = await getAllTechniques();
    const categoryLower = category.toLowerCase();
    
    // Filter techniques by category
    return Object.values(allTechniques).filter((technique) => {
      // Ensure technique is a valid TechniqueData object
      if (!technique || typeof technique !== 'object' || !('name' in technique)) {
        return false;
      }
      
      // Cast technique to the correct type
      const techniqueCast = technique as TechniqueData;
      
      const techniqueCategory = (techniqueCast.category || '').toLowerCase();
      return techniqueCategory === categoryLower;
    }) as TechniqueData[];
  } catch (error) {
    logger.error(`Error fetching techniques for category "${category}":`, error);
    throw error;
  }
};

export const getTechniquesByTag = async (tag: string): Promise<TechniqueData[]> => {
  try {
    const allTechniques = await getAllTechniques();
    const tagLower = tag.toLowerCase();
    
    // Filter techniques by tag
    return Object.values(allTechniques).filter((technique) => {
      // Ensure technique is a valid TechniqueData object
      if (!technique || typeof technique !== 'object' || !('name' in technique)) {
        return false;
      }
      
      // Cast technique to the correct type
      const techniqueCast = technique as TechniqueData;
      
      const tags = (techniqueCast.tags || []).map((t: string) => t.toLowerCase());
      return tags.includes(tagLower);
    }) as TechniqueData[];
  } catch (error) {
    logger.error(`Error fetching techniques for tag "${tag}":`, error);
    throw error;
  }
};



/**
 * Get community technique data (from techniques node, not personalWiki)
 */
export const getCommunityTechnique = async (techniqueKey: string): Promise<TechniqueData | null> => {
  try {
    // This function specifically gets the community/public wiki data
    return getTechniqueById(techniqueKey);
  } catch (error) {
    logger.error(`Error fetching community technique ${techniqueKey}:`, error);
    throw error;
  }
};

/**
 * Get public wiki information for a technique (from original Marune structure)
 */
export const getTWTPublicInfo = async (techniqueKey: string): Promise<any> => {
  try {
    const publicRef = ref(database, `trainingWikiTechniques/${techniqueKey}/public`);
    const snapshot = await get(publicRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    
    return null;
  } catch (error) {
    logger.error(`Error fetching public wiki info for ${techniqueKey}:`, error);
    throw error;
  }
};

/**
 * Add a photo to a technique in user's personal wiki
 */
export const addPhotoToWikiTechnique = async (
  userId: string,
  techniqueId: string,
  photoUrl: string,
  thumbnail?: string,
  caption?: string
): Promise<string> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _photosRef = ref(database, `personalWiki/${userId}/${techniqueId}/photos`);
    const photoData: TechniquePhoto = {
      url: photoUrl,
      thumbnail: thumbnail || photoUrl,
      dateAdded: Date.now(),
      caption: caption || ''
    };
    
    const photoKey = await createWithKey(`personalWiki/${userId}/${techniqueId}/photos`, photoData);
    
    return photoKey;
  } catch (error) {
    logger.error(`Error adding photo to technique ${techniqueId}:`, error);
    throw error;
  }
};

/**
 * Remove a photo from a technique in user's personal wiki
 */
export const removePhotoFromWikiTechnique = async (
  userId: string,
  techniqueId: string,
  photoId: string
): Promise<void> => {
  try {
    await removeData(`personalWiki/${userId}/${techniqueId}/photos/${photoId}`);
  } catch (error) {
    logger.error(`Error removing photo ${photoId} from technique ${techniqueId}:`, error);
    throw error;
  }
};


/**
 * Get all notes for a technique
 */
export const getWikiTechniqueNotes = async (
  userId: string,
  techniqueId: string
): Promise<Record<string, TechniqueNote>> => {
  try {
    const notesRef = ref(database, `personalWiki/${userId}/${techniqueId}/notes`);
    const snapshot = await get(notesRef);
    
    if (snapshot.exists()) {
      return snapshot.val();
    }
    
    return {};
  } catch (error) {
    logger.error(`Error fetching notes for technique ${techniqueId}:`, error);
    throw error;
  }
};


/**
 * Removes a technique from a user's Personal Wiki (along with all related data -
 * stats, notes, photos etc.) and from the usersTrainingWikiTechsList helper node
 * @param techKey Database key of technique to be removed
 */
export const removeTechFromWiki = async (techKey: string): Promise<void> => {
  const user = getCurrentUser();
  if (!user) {
    throw new Error('User not authenticated');
  }

  try {
    // Remove from both Wiki and List using multi-location update
    const updates = {
      [`personalWiki/${user.uid}/${techKey}`]: null,
      [`usersTrainingWikiTechsList/${user.uid}/${techKey}`]: null
    };
    
    const rootRef = ref(database);
    await update(rootRef, updates);
    logger.info(`Removed technique ${techKey} from user's personal wiki`);
  } catch (error) {
    logger.error('Error removing technique from wiki:', error);
    throw error;
  }
};

const exportedObject = {
  getAllTechniques,
  getTechniqueById,
  getUserTechniques,
  createTechnique,
  updateTechnique,
  deleteTechnique,
  saveTechniqueToCollection,
  removeTechniqueFromCollection,
  updateUserTechnique,
  searchTechniques,
  getTechniquesByCategory,
  getTechniquesByTag,
  addNoteToWikiTechnique,
  editWikiTechniqueNote,
  deleteWikiTechniqueNote,
  getCommunityTechnique,
  getTWTPublicInfo,
  addPhotoToWikiTechnique,
  removePhotoFromWikiTechnique,
  getWikiTechniqueNotes,
  getTWTData,
  addTechToWiki,
  removeTechFromWiki
};

export default exportedObject;